import {
  KeyboardEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Marker, Popup } from "react-leaflet";

import * as EsriGeoCode from "esri-leaflet-geocoder";

import * as gj from "geojson-utils";

import { useMediaQuery, useToast } from "@chakra-ui/react";

import { useTranslator } from "@eo-locale/react";

import { useRouter } from "next/router";

import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import { esriToken, toastDuration } from "../../constants";
import { EsriContext } from "../../context/esri-context";
import { EsriContextType } from "../../context/esri-context-type";

import {
  setIsMarkerInsidePolygon,
  setMarker
} from "@/redux/features/place-marker/slice";
import { isAnyMarkerInsidePolygon } from "../../utils/helpers";
import { setPinSearchFromUrl } from "@/redux/features/place-right-panel/slice";

const NewMarker = () => {
  const L: any = require("leaflet"); // causes typescript error in import
  const { mapRef, setActionsVisibility } = useContext(
    EsriContext
  ) as EsriContextType;
  const dispatch = useAppDispatch();

  const markerData = useAppSelector((state) => state.placesMarkerSlice.marker);
  const { address } = markerData;
  const isAddressVisible = useAppSelector(
    (state) => state.place.place.showAddress
  );
  const layers = useAppSelector((state) => state.placeLayers.layers);

  const [isDesktop] = useMediaQuery("(min-width: 768px)");

  const markerRef = useRef(null);
  const defaultLat = mapRef?.getCenter().lat;
  const defaultLng = mapRef?.getCenter().lng;

  const [position, setPosition] = useState<any>([defaultLat, defaultLng]);

  const toast = useToast();
  const { translate } = useTranslator();

  const createIcon = () => {
    const data = {
      id: "new icon",
      html: `
      <div style="display: flex; flex-direction: column; align-items: center;" tabindex="1" aria-label="${address}">
      <div class="icon-style " >
        <span
          class="marker-pin marker-icon-marker"
          style="color: #2b6cb0; font-size: 4.125rem"
        >
          <span
            class="marker-icon marker-shadow marker-blinking marker-icon-circle"
            style="color: #2b6cb0;font-size: 1rem;top: 0.95rem; left: 25.25px;"
          ></span>
        </span>
      </div>
      <div style="color: #fff;width: 140px;height: 20px;justify-content: center;text-align: center;background: #fff;color: black;border-radius: 8px;">  <a id="create-pin-link" href='#create-pin'>${translate(
        "places.dragPin"
      )} </a></div>
      </div>
      `
    };
    return new L.divIcon(data);
  };

  useEffect(() => {
    // focus on marker on page load
    const marker = document.getElementById("create-pin-link");

    marker?.focus();
  }, []);

  const isInsidePolygon = () => {
    toast({
      description: translate("places.pinOutsideBoundary"),
      isClosable: true,
      duration: toastDuration,
      status: "warning",
      containerStyle: {
        marginBottom: "1 !important"
      }
    });
  };

  const updateMarkerPosition = async (
    lat: number,
    lng: number,
    event?: any
  ) => {
    setPosition([lat, lng]);

    mapRef.setView([lat, lng]);

    const isPinInsidePolygon = await isAnyMarkerInsidePolygon(
      lng,
      lat,
      layers,
      gj
    );

    if (!isPinInsidePolygon) {
      dispatch(setIsMarkerInsidePolygon(false)); // marker is outside the polygon show the toast and render the new page

      (((event?.key === "n" || event?.key === "N") &&
        event?.shiftKey &&
        (event?.altKey || event?.ctrlKey)) ||
        !event) &&
        isInsidePolygon();
    } else {
      toast.closeAll();
      dispatch(setIsMarkerInsidePolygon(true));
    }

    setActionsVisibility(!isDesktop);

    const updatedMarkerData = {
      ...markerData,
      lat,
      lng
    };

    try {
      EsriGeoCode.reverseGeocode({
        apikey: esriToken
      })
        .latlng({ lat, lng })
        .run(function (error, result) {
          if (error) {
            return;
          }
          dispatch(
            setMarker({
              ...updatedMarkerData,
              address: result.address.LongLabel
            })
          );
        });
    } catch (error) {
      console.error("error", error);
    }
  };

  const eventHandlers = useMemo(
    () => ({
      async dragend() {
        // close if any toast is present
        toast.closeAll();

        const marker = markerRef.current;
        const lat = marker?.getLatLng()?.lat;
        const lng = marker?.getLatLng()?.lng;

        if (marker) {
          updateMarkerPosition(lat, lng);
        }
      }
    }),
    [markerData]
  );
  const router = useRouter();
  const { asPath } = router;
  useEffect(() => {
    const marker = markerRef.current;
    const lat = marker?.getLatLng()?.lat;
    const lng = marker?.getLatLng()?.lng;

    if (marker) {
      updateMarkerPosition(lat, lng);
      const updatedPath = asPath?.split("?")?.[0];
      dispatch(setPinSearchFromUrl(false));
      router.replace(updatedPath, "", { shallow: true });
    }
  }, []);

  useEffect(() => {
    const handleKeyDown: any = (event: KeyboardEvent) => {
      const marker = markerRef.current;
      if (!marker) return;

      const lat = marker.getLatLng().lat;
      const lng = marker.getLatLng().lng;
      const zoom = mapRef.getZoom();
      const baseStep = 0.01;

      // Adjust step size based on zoom level
      const step = baseStep / Math.pow(2, zoom - 10);
      const combo = event.shiftKey && event.key;

      switch (combo) {
        case "ArrowUp":
          updateMarkerPosition(lat + step, lng, event);
          break;
        case "ArrowDown":
          updateMarkerPosition(lat - step, lng, event);
          break;
        case "ArrowLeft":
          updateMarkerPosition(lat, lng - step, event);
          break;
        case "ArrowRight":
          updateMarkerPosition(lat, lng + step, event);
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [markerData]);

  return (
    <Marker
      autoPan
      ref={markerRef}
      position={position}
      eventHandlers={eventHandlers}
      draggable={true}
      icon={createIcon()}
      riseOnHover={true}
      riseOffset={90}
    >
      {isAddressVisible && address && <Popup>{address}</Popup>}
    </Marker>
  );
};

export default NewMarker;
